"use client";

import { useEffect, useState } from "react";

interface ResponsiveLimits {
	/** Number of cards to show initially */
	initialLimit: number;
	/** Number of images that should get priority loading */
	priorityCount: number;
}

/**
 * Hook that returns responsive limits for festival cards based on screen size
 * Desktop: 8 cards, Tablet: 6 cards, Mobile: 4 cards
 * Priority count is optimized for above-the-fold images in each layout
 */
export function useResponsiveLimit(): ResponsiveLimits {
	const [limits, setLimits] = useState<ResponsiveLimits>({
		initialLimit: 8, // Default to desktop
		priorityCount: 4,
	});

	useEffect(() => {
		const updateLimits = () => {
			const width = window.innerWidth;

			if (width < 768) {
				// Mobile: 2x2 grid, first 2 images above fold
				setLimits({ initialLimit: 4, priorityCount: 2 });
			} else if (width < 1024) {
				// Tablet: 2x3 grid, first 4 images above fold
				setLimits({ initialLimit: 6, priorityCount: 4 });
			} else {
				// Desktop: 2x4 grid, first 4 images above fold
				setLimits({ initialLimit: 8, priorityCount: 4 });
			}
		};

		// Set initial limits
		updateLimits();

		// Listen for resize events
		window.addEventListener("resize", updateLimits);

		return () => window.removeEventListener("resize", updateLimits);
	}, []);

	return limits;
}

/**
 * Hook optimized for artists page with higher density grid (2-4 columns)
 * Mobile: 8 artists, Tablet: 12 artists, Desktop: 16 artists
 * Priority counts are higher due to more items above the fold
 */
export function useResponsiveArtistLimit(): ResponsiveLimits {
	const [limits, setLimits] = useState<ResponsiveLimits>({
		initialLimit: 16, // Default to desktop
		priorityCount: 8,
	});

	useEffect(() => {
		const updateLimits = () => {
			const width = window.innerWidth;

			if (width < 640) {
				// Mobile: 2x4 grid, first 6 images above fold
				setLimits({ initialLimit: 8, priorityCount: 6 });
			} else if (width < 768) {
				// Small: 3x4 grid, first 9 images above fold
				setLimits({ initialLimit: 12, priorityCount: 9 });
			} else {
				// Medium+: 4x4 grid, first 8 images above fold
				setLimits({ initialLimit: 16, priorityCount: 8 });
			}
		};

		// Set initial limits
		updateLimits();

		// Listen for resize events
		window.addEventListener("resize", updateLimits);

		return () => window.removeEventListener("resize", updateLimits);
	}, []);

	return limits;
}

/**
 * Server-side compatible version that returns default desktop limits
 * Use this for SSR/SSG where window is not available
 */
export function getServerResponsiveLimit(): ResponsiveLimits {
	return {
		initialLimit: 8, // Default to desktop for SSR
		priorityCount: 4,
	};
}

/**
 * Server-side compatible version for artists page
 */
export function getServerResponsiveArtistLimit(): ResponsiveLimits {
	return {
		initialLimit: 16, // Default to desktop for SSR
		priorityCount: 8,
	};
}

/**
 * Hook optimized for festival artists tab with higher density grid (2-6 columns)
 * Mobile: 12 artists, Tablet: 24 artists, Desktop: 36 artists
 * This matches the artist discovery page requirements
 */
export function useResponsiveFestivalArtistLimit(): ResponsiveLimits {
	const [limits, setLimits] = useState<ResponsiveLimits>({
		initialLimit: 36, // Default to desktop
		priorityCount: 18, // First 3 rows on desktop
	});

	useEffect(() => {
		const updateLimits = () => {
			const width = window.innerWidth;

			if (width < 640) {
				// Mobile: 2 columns, 6 rows = 12 artists
				setLimits({ initialLimit: 12, priorityCount: 6 });
			} else if (width < 1024) {
				// Tablet: 3-4 columns, 6-8 rows = 24 artists
				setLimits({ initialLimit: 24, priorityCount: 12 });
			} else {
				// Desktop: 5-6 columns, 6 rows = 36 artists
				setLimits({ initialLimit: 36, priorityCount: 18 });
			}
		};

		// Set initial limits
		updateLimits();

		// Listen for resize events
		window.addEventListener("resize", updateLimits);

		return () => window.removeEventListener("resize", updateLimits);
	}, []);

	return limits;
}
